import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";

import { registerLoader, registerUser } from "../../store/actions/authAction";
import {
  checkCoupon,
  getFetchCustomer,
  updatePlan,
} from "../../store/actions/subscribeAction";
import {
  billingAddressFields,
  getStripeToken,
  inputWhenLogin,
  inputWithoutLogin,
} from "../../utils/data";
import { US_STATES } from "../../utils/usStates";
import CardOptions from "./CardOptions";
import { ACCOUNT_API } from "../account/accountNavItems";
import { STRIPE_PUBLIC_KEY } from "../../constant";
import { discountedPrice, discountDuration } from "../../utils/discountPrice";
import SpinnerIcon from "./SpinnerIcon";

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const Subscribe = (props) => {
  const location = useLocation();
  // `location.state` is undefined on a direct load or refresh, so guard the
  // destructuring rather than throwing and white-screening checkout. The
  // missing-plan case is handled by the redirect below.
  const { priceId } = location.state || {};
  // The license box starts unchecked, so a customer actively agrees before
  // paying (Dave, 2026-10-02). The "required" warning waits until they have
  // touched the box; on first load the disabled button is the only cue.
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreeTouched, setAgreeTouched] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { authLoading, user, uid, couponLoading, regLoading } = useSelector(
    (state) => state.authUser
  );

  const initialData = {
    phone: "",
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    dateOfBirth: "",
    userName: "",
    // Most customers are not buying for a company. They can overwrite this,
    // and a cleared field is still sent as "Self" (see handleSignUp).
    companyName: "Self",
    line1: "",
    city: "",
    state: "",
    postalCode: "",
  };
  const [registerData, setregisterData] = useState(initialData);

  const [webaddress, setWebaddress] = useState("");
  const [coupon, setCoupon] = useState("");
  const [validCoupon, setValidCoupon] = useState(false);
  /*
      The discount itself, not just "is it valid" (static#74). The server
      answers with what the code is worth; this drives the summary price and
      whether a card is required at all.
  */
  const [discount, setDiscount] = useState(null);

  /*
      Display only. Stripe works out the real amount from the discount
      attached to the subscription, and the backend re-resolves the code at
      subscribe time — nothing computed in the browser is sent or trusted.
  */
  const discounted = discountedPrice(props.price, discount);
  const priceToday = discounted || props.price;
  // A code that only covers the first payment must not be shown as the
  // renewal price, or the second invoice is a surprise.
  const renewalPrice =
    discount?.duration === "forever" && discounted ? discounted : props.price;
  const discountWindow = discountDuration(discount);
  const freeForever = Boolean(discount?.coversEverything);
  const [selectedOption, setSelectedOption] = useState("ExistingCard");
  const [cardDetails, setCardDetails] = useState("");

  const isSample = props.course === "Sample Golf Course";

  const [inputData, setInputData] = useState(
    user ? inputWhenLogin : inputWithoutLogin
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setregisterData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Shared renderer for the profile and billing field groups. `md`/`colClass`
  // come from the field definition so State and ZIP can share a row.
  const renderField = (obj) => (
    <Col
      key={obj.name}
      md={obj.md || "12"}
      className={`${obj.colClass || "p-0"} mb-3`}
    >
      <Label for={obj.name} className="sub-form-label">
        {obj.label}
      </Label>
      {obj.type === "select" ? (
        <Select
          inputId={obj.name}
          className="signIn__input"
          name={obj.name}
          placeholder={obj.placeholder}
          isSearchable={true}
          isClearable={true}
          // match the height of the text inputs
          styles={{
            control: (base) => ({
              ...base,
              minHeight: 50,
            }),
          }}
          options={US_STATES}
          value={
            US_STATES.find((o) => o.value === registerData[obj.name]) || null
          }
          onChange={(opt) =>
            setregisterData((prev) => ({
              ...prev,
              [obj.name]: opt?.value || "",
            }))
          }
        />
      ) : (
        <Input
          id={obj.name}
          className="signIn__input py-4"
          type={obj.type}
          name={obj.name}
          autoComplete={obj.autocomplete}
          placeholder={obj.placeholder}
          value={registerData[obj.name]}
          onChange={handleInputChange}
        />
      )}
    </Col>
  );

  const isValidCoupon = (event) => {
    event.preventDefault();

    dispatch(
      checkCoupon(coupon, (res) => {
        setDiscount(res);
        setValidCoupon(Boolean(res));
      })
    );
  };
  const createStripePayload = async (elements, stripe) => {
    if (priceId === "test") {
      return { priceId, userId: uid };
    } else if (
      user &&
      user?.stripe_customer_id &&
      selectedOption === "ExistingCard"
    ) {
      return {
        customerId: user.stripe_customer_id,
        priceId: priceId,
        userId: uid,
      };
    } else {
      const billing = {
        name: `${registerData?.firstName || ""} ${
          registerData?.lastName || ""
        }`.trim(),
        line1: registerData?.line1,
        city: registerData?.city,
        state: registerData?.state,
        postalCode: registerData?.postalCode,
      };
      const stripeToken = await getStripeToken(elements, stripe, billing);
      return {
        token: stripeToken,
        priceId: priceId,
        userId: uid,
        // Billing address for Stripe Tax (backend attaches it to the
        // Stripe Customer and enables automatic_tax). US-only for now.
        line1: billing.line1,
        city: billing.city,
        state: billing.state,
        postalCode: billing.postalCode,
      };
    }
  };
  const createUserObject = () => {
    if (user) {
      return {
        fullname: registerData?.firstName + " " + registerData?.lastName,
        phone1: registerData?.phone,
        date_of_birth: user?.date_of_birth,
      };
    } else {
      return {
        username: registerData?.userName,
        password: registerData?.password,
        email: registerData?.email,
        fullname: registerData?.firstName + " " + registerData?.lastName,
        phone1: registerData?.phone,
        date_of_birth: registerData?.dateOfBirth,
      };
    }
  };

  const onCompleted = (message) => {
    toast.success(message);
    setWebaddress("");
    setregisterData(initialData);
    dispatch(registerLoader(false));
    /*
        Straight to API Management, not to the settings landing page. The
        thing a customer wants the moment a subscription goes through is the
        API credentials they just paid for, and that is the one page that can
        create them.
    */
    history.push(ACCOUNT_API);
  };
  // Fields that must be filled to register. Signed-in users keep the date of
  // birth already on their account, and their email / password / account ID
  // are not part of this form.
  const requiredProfileFields = user
    ? [
        { name: "firstName", label: "First Name" },
        { name: "lastName", label: "Last Name" },
        { name: "phone", label: "Phone Number" },
      ]
    : [
        { name: "firstName", label: "First Name" },
        { name: "lastName", label: "Last Name" },
        { name: "email", label: "Email Address" },
        { name: "password", label: "Password" },
        { name: "userName", label: "Account ID" },
        { name: "phone", label: "Phone Number" },
        { name: "dateOfBirth", label: "Date of Birth" },
      ];

  const handleSignUp = async (e, elements, stripe) => {
    e.preventDefault();

    if (registerData?.password !== registerData.confirmPassword && !user) {
      toast.error("Passwords must be the same");
      return;
    }
    // #26: require a complete US billing address whenever a new card is
    // entered (skipped for the free "test" plan and existing-card reuse).
    const usingNewCard =
      priceId !== "test" &&
      !(user?.stripe_customer_id && selectedOption === "ExistingCard");
    if (usingNewCard) {
      const missingAddress = ["line1", "city", "state", "postalCode"].some(
        (k) => !registerData?.[k]
      );
      if (missingAddress) {
        toast.error("Please enter your complete billing address.");
        return;
      }
    }
    // Check the form fields before tokenizing the card, and name the ones
    // that are blank. The old check ran over the built payload, where
    // `fullname` is `first + " " + last` and so is never empty, and the
    // toast never said which field to look at.
    const missing = requiredProfileFields
      .filter(({ name }) => !registerData?.[name])
      .map(({ label }) => label);
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(", ")}.`);
      return;
    }

    dispatch(registerLoader(true));
    const stripePayload = await createStripePayload(elements, stripe);

    const obj = createUserObject();

    const paymentConfirmation = Object.values(stripePayload).some(
      (value) => value === "" || value === undefined
    );
    /*
        A 100%-forever code means Stripe never bills this subscription, and it
        will create one with no payment method at all — so demanding a card
        would block the comp path (backend#103) for no reason. Proven in Stripe
        test mode: subtotal 9.00 USD, total 0.00 USD, invoice paid, no card.

        Only `forever`. A 100% code lasting one month bills normally from the
        second invoice, so that customer still needs a card on file.
    */
    const noPaymentNeeded = Boolean(discount?.coversEverything);
    if (paymentConfirmation && priceId !== "test" && !noPaymentNeeded) {
      // clear the spinner, or the button stays disabled until reload
      dispatch(registerLoader(false));
      toast.error("Error: payment Required.");
    } else {
      const userData = {
        ...obj,
        webaddress: webaddress,
        company: registerData?.companyName?.trim() || "Self",
      };
      const payload = validCoupon
        ? { ...stripePayload, coupon }
        : stripePayload;
      if (user) {
        dispatch(
          updatePlan(userData, payload, user.stripe_customer_id, () => {
            onCompleted("Updated Successfully");
          })
        );
      } else {
        // console.log("Payload ::", userData);
        dispatch(
          registerUser(userData, payload, () => {
            onCompleted("Registered Successfully");
          })
        );
      }
    }
  };

  useEffect(() => {
    dispatch(registerLoader(false));
    if (user) {
      if (user?.stripe_customer_id) {
        dispatch(
          getFetchCustomer(user?.stripe_customer_id, (res) => {
            setCardDetails(res);
          })
        );
      }
      const name = user.fullname?.split(" ") || [""];
      setregisterData((prev) => ({
        ...prev,
        phone: user?.phone1,
        email: user?.email,
        firstName: name[0],
        lastName: name[1],
        companyName: user?.company || "Self",
      }));
      setWebaddress(user?.webaddress);
    }
  }, []);

  // No plan came through on the navigation state (direct load or refresh),
  // so there is nothing to check out. The single/all price ids come from
  // Stripe at runtime and cannot be derived from the path, so send the
  // customer back to the plans page to pick one.
  if (!priceId) {
    return <Redirect to="/api/plans" />;
  }

  return (
    <>
      <Elements stripe={stripePromise}>
        <ElementsConsumer>
          {({ elements, stripe }) => (
            <Row className="w-100 m-0 py-5 px-lg-narrow justify-content-center bg-white">
              <Col className="p-0">
                <div className="text-center">
                  <h1 className="fs--32  fw--700 font-poppins">
                    Golf Course Maps API
                  </h1>
                  <h6 className="fs--16 fw--500 text-muted font-poppins pt-2">
                    {" "}
                    {props.subtitle}
                  </h6>
                </div>

                <Row className="py-5">
                  <Col xs={12} lg={6} className="px-5 mb-5">
                    <Row>
                      <Col className="p-0" md="12">
                        <div className="my-3">
                          <h4 className="fs--18 fw--600 font-poppins">
                            Create Your API Profile
                          </h4>
                        </div>
                      </Col>
                    </Row>
                    <Row className="sub-inputs">
                      {inputData?.map(renderField)}
                      <Col md="12" className="p-0 mb-3">
                        <FormGroup>
                          <Label className="sub-form-label">
                            Website (Not Required)
                          </Label>
                          <Input
                            className="signIn__input py-4"
                            placeholder="Web Address"
                            type="text"
                            value={webaddress}
                            onChange={(e) => setWebaddress(e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {!isSample && (
                      <>
                        <Row>
                          <Col className="p-0" md="12">
                            <div className="my-2">
                              <h4 className="fs--18 fw--600 font-poppins">
                                Billing Information
                              </h4>
                            </div>
                          </Col>
                        </Row>
                        {/* // Stripe */}

                        {/* card option  */}

                        {/* ///// */}
                        {cardDetails && (
                          <>
                            <CardOptions
                              selectedOption={selectedOption}
                              setSelectedOption={setSelectedOption}
                            />
                            {selectedOption === "ExistingCard" && (
                              <div className="p-2 border d-flex justify-content-between rounded">
                                <small className="mb-0">
                                  {cardDetails?.brand}
                                </small>
                                <small className="mb-0">
                                  ******
                                  {cardDetails?.last4}
                                </small>
                              </div>
                            )}
                          </>
                        )}
                        {/* stripe */}
                        {(selectedOption === "NewCard" || !cardDetails) && (
                          <>
                            <Row className="sub-inputs">
                              {billingAddressFields.map(renderField)}
                            </Row>
                            <Row>
                              <Col className="px-0 mb-2">
                                <Label className="sub-form-label">
                                  Card Number
                                </Label>
                                <CardNumberElement
                                  required
                                  options={{
                                    placeholder: "Enter card number",
                                    showIcon: true,
                                    style: {
                                      base: {
                                        // backgroundColor: "#232733",
                                        fontSize: "16px",
                                      },
                                      invalid: {
                                        color: "#9e2146",
                                      },
                                    },
                                  }}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col className="pl-0 pr-2">
                                <Label className="sub-form-label">
                                  Expires
                                </Label>
                                <CardExpiryElement
                                  required
                                  options={{
                                    placeholder: "MM/YY",
                                    showIcon: true,
                                    style: {
                                      base: {
                                        fontSize: "16px",
                                      },
                                      invalid: {
                                        color: "#9e2146",
                                      },
                                    },
                                  }}
                                />
                              </Col>
                              <Col className="pr-0 pl-2">
                                <Label className="sub-form-label">CVC</Label>
                                <CardCvcElement
                                  required
                                  options={{
                                    placeholder: "CVC",
                                    showIcon: true,
                                    style: {
                                      base: {
                                        fontSize: "16px",
                                      },
                                      invalid: {
                                        color: "#9e2146",
                                      },
                                    },
                                  }}
                                />
                              </Col>
                            </Row>{" "}
                            {/* // add coupon  */}
                            <Row>
                              <Col className="p-0 my-1">
                                <Input
                                  className={`signIn__input py-4 mt-1 ${
                                    validCoupon
                                      ? "text-site-primary"
                                      : "text-dark"
                                  }`}
                                  type="text"
                                  name="coupon"
                                  placeholder="Coupon Code"
                                  disabled={couponLoading}
                                  value={coupon}
                                  onChange={(e) => {
                                    setCoupon(e.target.value);
                                    // The summary and the checkout payload must
                                    // never disagree. Editing this box after a
                                    // code was accepted left `discount` showing
                                    // the old code's price while checkout sent
                                    // the newly typed text (#87 review). Clear
                                    // on edit so the customer re-checks the code
                                    // they actually typed.
                                    if (validCoupon || discount) {
                                      setValidCoupon(false);
                                      setDiscount(null);
                                    }
                                  }}
                                />
                              </Col>
                              <Col className="p-0">
                                <Button
                                  disabled={!coupon}
                                  className={`border border-none float-right coupon-button my-2 ${
                                    coupon ? "btn-success" : "btn-secondary"
                                  }`}
                                  onClick={(event) => isValidCoupon(event)}
                                >
                                  {couponLoading ? (
                                    <SpinnerIcon />
                                  ) : (
                                    "Apply coupon"
                                  )}
                                </Button>
                              </Col>
                            </Row>
                            {/* //// */}
                          </>
                        )}
                        {/* ///// */}
                      </>
                    )}
                    <Row className="my-5">
                      <Col className="p-0" xs="2">
                        <Input
                          type="checkbox"
                          className="mx-2"
                          checked={isAgreed}
                          onChange={() => {
                            setIsAgreed(!isAgreed);
                            setAgreeTouched(true);
                          }}
                        ></Input>
                      </Col>
                      <Col className="p-0" xs="10">
                        <div>
                          {" "}
                          {isSample ? (
                            <p className="fw--400 font-poppins text-muted  ml-4">
                              I agree to the{" "}
                              <Link
                                to="/api/license"
                                className="text-site-primary  fw--500"
                              >
                                Golfbert API License Agreement
                              </Link>{" "}
                            </p>
                          ) : (
                            <span className="fs--14 fw--400 font-poppins text-muted  ml-1">
                              I agree to the{" "}
                              <Link
                                to="/api/license"
                                className="text-site-primary"
                              >
                                Golfbert API License Agreement
                              </Link>{" "}
                              and understand that this is a recurring
                              subscription. I will be billed automatically on a
                              recurring basis until I cancel.
                            </span>
                          )}
                          {!isAgreed && agreeTouched && (
                            <p className="text-danger fs--12 font-poppins p-2 mt-3 border border-danger">
                              *This field is required.
                            </p>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      {isSample ? (
                        <Col className="p-0 mb-4" md="12">
                          <span className="fs--16 fw--400 text-justify font-poppins text-muted">
                            Once we have reviewed your request, we will issue
                            you an API Key that will give you access to our
                            sample course. If you have any questions, please
                            refer to our{" "}
                            <Link
                              to="/api/guides"
                              className="text-site-primary  fw--500"
                            >
                              Guides
                            </Link>{" "}
                            or you can{" "}
                            <Link
                              to="/api/support"
                              className="text-site-primary fw--500"
                            >
                              contact us
                            </Link>{" "}
                            for further assistance.
                          </span>
                        </Col>
                      ) : (
                        <> {}</>
                      )}
                    </Row>
                    <Row>
                      <Col className="px-0">
                        <div className="text-center">
                          <Button
                            disabled={regLoading || !isAgreed}
                            className="text-dark rounded-1 btn btn-site-bgGreen px-5 py-3 btn btn-secondary subscribe-form-button"
                            onClick={(e) => {
                              handleSignUp(e, elements, stripe);
                            }}
                          >
                            {regLoading ? (
                              <SpinnerIcon />
                            ) : isSample ? (
                              "Create API account"
                            ) : (
                              "Subscribe to API"
                            )}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={12} lg={6}>
                    <div className="purchase-summary p-3">
                      <h4 className="fs--18 fw--600 font-poppins">
                        Purchase Summary
                      </h4>
                      <ul className="sub-details">
                        {/* Nothing is charged or renewed on the free plan. */}
                        {!isSample && (
                          <li>
                            <h6 className="fs--14 fw--400 font-poppins text-muted">
                              {freeForever ? (
                                <>
                                  Your code covers this plan in full, so there
                                  is nothing to pay and no card is needed. It
                                  applies to every renewal, not just the first,
                                  so your subscription stays free while the code
                                  is active.
                                </>
                              ) : (
                                <>
                                  Your credit card will be charged{" "}
                                  <strong>{priceToday}</strong>
                                  {discounted && discountWindow
                                    ? ` (${discount.code} applied ${discountWindow})`
                                    : ""}{" "}
                                  and will automatically renew monthly at{" "}
                                  {renewalPrice} until you notify us otherwise.
                                  This subscription is non-refundable.
                                </>
                              )}
                            </h6>
                          </li>
                        )}
                        {props.course === "Single Golf Course" && (
                          <li className="my-4">
                            <Row>
                              <Col
                                md="12"
                                className="fs--14 fw--400 font-poppins text-muted"
                              >
                                <div>
                                  <strong>{props.singleCourse} </strong>
                                  Before proceeding, please{" "}
                                  <Link
                                    to="/courses/search"
                                    className="text-site-primary "
                                  >
                                    search for your preferred course
                                  </Link>{" "}
                                  to confirm we have it mapped. If your course
                                  is not yet mapped, our mapping team will
                                  prioritize it to the top of the queue, in the
                                  order it was received.
                                </div>
                              </Col>
                            </Row>
                          </li>
                        )}
                        {props.course === "All Golf Courses" && (
                          <li className="my-4">
                            <Row>
                              <Col
                                md="12"
                                className="fs--14 fw--400 font-poppins text-muted"
                              >
                                <div className="warning-block">
                                  <Row>
                                    <Col sm="2" className="fs--48">
                                      <i className="fa-solid fa-triangle-exclamation"></i>
                                    </Col>
                                    <Col sm="10">
                                      <strong>{props.allCourses} </strong>
                                      <br />
                                      <p>
                                        Do not attempt to call the API to
                                        retrieve the course data for the entire
                                        database.
                                      </p>
                                      <p>
                                        This approach isn't very efficient,
                                        could introduce security risks, and does
                                        not align with the{" "}
                                        <Link
                                          to="/api/license"
                                          className="text-site-primary "
                                        >
                                          API Agreement
                                        </Link>
                                        . Instead please call the API on a
                                        per-course basis, only when a user
                                        actually needs access to a specific
                                        course.
                                      </p>
                                      <p>
                                        If your account hits the daily maximum
                                        for several days in a row, it may be
                                        temporarily disabled for security
                                        reasons.
                                      </p>
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                            </Row>
                          </li>
                        )}
                        <li className="fs--14 fw--400 font-poppins text-muted">
                          Golf Course Maps API — {props.course}
                        </li>
                        <li className="fs--14 fw--400 font-poppins text-muted">
                          {discounted ? (
                            <>
                              <s className="text-muted mr-1">{props.price}</s>
                              <strong>{priceToday}</strong> today (+ applicable
                              tax)
                            </>
                          ) : (
                            <>
                              {props.price} today
                              {!isSample && " (+ applicable tax)"}
                            </>
                          )}
                        </li>
                        {props.course === "All Golf Courses" && (
                          <li className="fs--14 fw--400 font-poppins text-muted">
                            Monthly limit: 100,000 calls. Daily limit: 3,572
                            calls.
                          </li>
                        )}
                        <li>
                          <h6 className="fs--14 mt-4 fw--600  font-poppins text-site-primary">
                            Head to the next screen to manage your API keys.
                          </h6>
                        </li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </ElementsConsumer>
      </Elements>
    </>
  );
};

export default Subscribe;
